import React, { useState, useEffect } from 'react';
import styles from './AdditionalDataPage.module.css';
import { getEnvironmentApiUrl } from '../../services/General';
import useHttp from '../../hooks/useHttp';
import usePatientStore from '../../stores/patient-store';

// Hardcoded dev values (to be cleaned up later as you mentioned)
// const customTokenHardcoded = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1aWQiOiJFTkZFUk1FUklBIiwiZW1haWwiOiIiLCJyb2xlX2NvZGVzIjpbImVuZmVybWVyaWEiXSwiaWF0IjoxNzc2MjUyMTA3LCJleHAiOjE3NzYyNTU3MDcsImF1ZCI6ImJjX2hvbWUiLCJpc3MiOiJiYWNrZW5kX2NvcmUiLCJzdWIiOiJiYmZhYzQzYi0xZjQ3LTRlZTgtYWI1OC0yOWE3YWM0ZjdkZDkifQ.tppSGn8l0l6fMLMxeeyZ1b6KaWDH0Y291oz5Rc7ELyY';
// const devUrl = 'https://portal-pre.sad40.com:4200/api/v1/';
// const devPatientId = 98;

// --- TYPES & INTERFACES ---

// 1. Table Data Interface
interface TableRowData {
    id: string;
    date: string;
    profile?: string;
    createdBy?: string;
    modDate?: string;
    modBy?: string;
    wsocialFilePath?: string;
}

// 2. API Response Interfaces (Based on Swagger)
interface ApiFileOwner {
    DisplayName: string;
    ID: string;
}

interface ApiFileRestoreStatus {
    IsRestoreInProgress: boolean;
    RestoreExpiryDate: string;
}

interface ApiFileMetadata {
    key: string;
    contentType: string;
    contentLength: number;
    lastModified: string;
    userMetadata: Record<string, string>; // additionalProp1, etc.
}

interface ApiFileResponse {
    Key: string;
    LastModified: string;
    ETag: string;
    ChecksumAlgorithm: string[];
    ChecksumType: string;
    Size: number;
    StorageClass: string;
    Owner: ApiFileOwner;
    RestoreStatus: ApiFileRestoreStatus;
    metadata: ApiFileMetadata;
}

interface CollapsibleSectionProps {
    title: string;
    defaultOpen?: boolean;
    children: React.ReactNode;
}

interface AdditionalDataTableProps {
    data: TableRowData[];
    isLoading?: boolean;
    onAccessClick?: (filePath: string) => void;
}

// --- SUB-COMPONENTS ---

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, defaultOpen = false, children }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader} onClick={() => setIsOpen(!isOpen)}>
                <span className={styles.toggleIcon}>{isOpen ? '-' : '+'}</span>
                <span className={styles.sectionTitle}>{title}</span>
            </div>
            {isOpen && <div className={styles.sectionContent}>{children}</div>}
        </div>
    );
};

const AdditionalDataTable: React.FC<AdditionalDataTableProps> = ({ data, isLoading, onAccessClick }) => {
    return (
        <div className={styles.tableWrapper}>
            <table className={styles.dataTable}>
                <thead>
                    <tr>
                        <th>Fecha</th>
                        {/* <th>Perfil</th>
                        <th>Creado por</th>
                        <th>Fecha de modificación</th>
                        <th>Modificado por</th> */}
                        <th className={styles.actionColumn}></th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan={6} className={styles.emptyState}>Cargando datos...</td>
                        </tr>
                    ) : data.length > 0 ? (
                        data.map((row) => (
                            <tr key={row.id}>
                                <td>{row.date}</td>
                                {/* <td>{row.profile}</td>
                                <td>{row.createdBy}</td>
                                <td>{row.modDate}</td>
                                <td>{row.modBy}</td> */}
                                <td className={styles.actionColumn}>
                                    <button 
                                        className={styles.accessButton}
                                        onClick={() => onAccessClick && row.wsocialFilePath && onAccessClick(row.wsocialFilePath)}
                                        disabled={!row.wsocialFilePath} // Disable if no path is available
                                    >
                                        Acceder
                                    </button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={6} className={styles.emptyState}>No hay datos disponibles</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

// --- MAIN COMPONENT ---

const AdditionalDataPage: React.FC = () => {
    const patient = usePatientStore(state => state.patient);
    const patientId = patient?.id ?? null;

    const [wsocialData, setWsocialData] = useState<TableRowData[]>([]);
    const [hc3Data, setHc3Data] = useState<TableRowData[]>([]);

    // Hooks for API calls
    const { isLoading: isLoadingWSocial, sendRequest: getWSocial } = useHttp<ApiFileResponse[]>();
    const { isLoading: isLoadingHC3, sendRequest: getHC3 } = useHttp<ApiFileResponse[]>();
    const { sendRequest: fetchSignedUrl } = useHttp<any>(); // Hook for the new signed URL call

    // Helper function to map API response to Table format
    const mapApiToTableData = (apiData: ApiFileResponse[]): TableRowData[] => {
        return apiData.map((item) => {
            const createdDate = new Date(item.LastModified).toLocaleDateString('es-ES');
            const modifiedDate = item.metadata?.lastModified 
                ? new Date(item.metadata.lastModified).toLocaleDateString('es-ES') 
                : createdDate;

            const userMeta = item.metadata?.userMetadata || {};

            return {
                id: item.Key,
                date: createdDate,
                profile: userMeta.profile || 'N/A', 
                createdBy: userMeta.createdBy || 'N/A',
                modDate: modifiedDate,
                modBy: userMeta.modifiedBy || 'N/A',
                wsocialFilePath: item?.metadata?.key.substring(item?.metadata?.key.indexOf('wsocial/')) ?? '',
            };
        });
    };

    // Handler for clicking "Acceder"
    const handleAccessFile = (filePath: string) => {
        if (!filePath) return;

        // const currentUrl = `${devUrl}patient/${devPatientId}/files/signed_url?path=${filePath}`;
        const currentUrl = `${getEnvironmentApiUrl()}patient/${patientId}/files/signed_url?path=${filePath}`;

        fetchSignedUrl({
            url: currentUrl,
            method: 'GET',
            // customToken: customTokenHardcoded
        }, (signedUrl) => {
            if (signedUrl) {
                // Open the returned URL in a new tab securely
                window.open(signedUrl, '_blank', 'noopener,noreferrer');
            }
        });
    };

    useEffect(() => {
        if (!patientId) return;

        // const currentUrl = `${devUrl}patient/${devPatientId}/files/list?path=wsocial`;
        const currentUrl = `${getEnvironmentApiUrl()}patient/${patientId}/files/list?path=wsocial`;

        // --- FETCH WSOCIAL DATA ---
        getWSocial({
            url: currentUrl,
            method: 'GET',
            // customToken: customTokenHardcoded
        }, (data) => {
            if (data && data.length > 0) {
                setWsocialData(mapApiToTableData(data));
            }
        });

        // --- FETCH HC3 DATA ---
        getHC3({
            url: `${getEnvironmentApiUrl()}patient/${patientId}/files/list?path=hc3`,
            method: 'GET',
        }, (data) => {
            if (data && data.length > 0) {
                setHc3Data(mapApiToTableData(data));
            }
        });
       
    }, [patientId]); 

    return (
        <div className={styles.pageContainer}>
            <CollapsibleSection title="wSocial" defaultOpen={true}>
                <AdditionalDataTable 
                    data={wsocialData} 
                    isLoading={isLoadingWSocial} 
                    onAccessClick={handleAccessFile} 
                />
            </CollapsibleSection>

            <CollapsibleSection title="HC3" defaultOpen={false}>
                <AdditionalDataTable 
                    data={hc3Data} 
                    isLoading={isLoadingHC3} 
                    onAccessClick={handleAccessFile} 
                />
            </CollapsibleSection>
        </div>
    );
};

export default AdditionalDataPage;